import React from 'react';
import { useContent } from '../contexts/ContentContext';
import { useIllustrationIndex } from '../hooks/useIllustrationIndex';
import { SEARCH_KEYS } from '../utils/publicSearch';
import { attributeGuideSections } from '../utils/guideHelp';
import GuideHelpButton from './GuideHelpButton';

export default function AttributePicker({ params, onChange }: {
    params: URLSearchParams;
    onChange: (key: string, value: string, source: HTMLElement) => void;
}) {
    const { content, language, t } = useContent();
    const { data, error, retry } = useIllustrationIndex();
    const primary = ['character', 'theme', 'scene', 'signature', 'rating'];
    const keys = [...primary, ...SEARCH_KEYS.filter(key => !primary.includes(key))];
    if (error) return <div role="alert"><p>{t('search.error')}</p><button className="lab-button" onClick={retry}>{t('common.retry')}</button></div>;
    if (!data) return <p role="status">{content.ui[language].tagSearch.loading}</p>;
    return <div className="lab-attribute-picker">
        {keys.map(key => {
            const label = content.ui[language].search.group[key as keyof typeof content.ui.jp.search.group] || key;
            const values = key === 'rating' ? data.ratings.slice().sort((a, b) => b.value - a.value).map(item => ({ slug: String(item.value), label: `★${item.value}`, count: item.count })) : data.groups[key]?.values || [];
            const selected = params.get(key) || '';
            const help = attributeGuideSections[key];
            return <fieldset key={key} data-attribute-group={key} className={primary.includes(key) ? 'lab-attribute-primary' : 'lab-attribute-secondary'}>
                <legend>{label}</legend>
                {help && <GuideHelpButton section={help} />}
                {primary.includes(key) ? <div className="lab-attribute-options">
                    {values.map(value => <button type="button" key={value.slug} aria-pressed={selected === value.slug}
                        onClick={event => onChange(key, selected === value.slug ? '' : value.slug, event.currentTarget)}>
                        {value.label} <small>({value.count})</small>
                    </button>)}
                    {values.length === 0 && <span>{content.ui[language].tagSearch.noSuggestions}</span>}
                </div> : <select aria-label={label} value={selected} onChange={event => onChange(key, event.target.value, event.currentTarget)}>
                    <option value="">{t('searchPublicSearchGallery.text1')}</option>
                    {selected && !values.some(value => value.slug === selected) && <option value={selected}>{selected}</option>}
                    {values.map(value => <option key={value.slug} value={value.slug}>{value.label} ({value.count})</option>)}
                </select>}
            </fieldset>;
        })}
    </div>;
}
